import { useState, useMemo, useEffect, useRef } from 'react';
import { useDynamicTableHeight } from '@/hooks/use-dynamic-table-height';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCurrency, formatHours, formatPercent, getVarianceColor } from '@/lib/budget-utils';
import { cn } from '@/lib/utils';
import { getStatusColor } from '@/lib/status-colors';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface CostCentre {
  id: number;
  code: string;
  name: string;
  type?: string;
  billable: boolean;
  costsBudget: number;
  actualCosts: number;
  committedCosts: number;
  revenueBudget: number;
  actualRevenue: number;
  hoursBudget: number;
  actualHours: number;
  lastUpdated?: Date;
  owner?: string;
  description?: string;
  status?: 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';
  isActive?: boolean; // Active/Inactive status toggle
  customFields?: {
    [key: string]: string | number | boolean | Date;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

interface ColumnDefinition {
  key: keyof CostCentre | 'totalCosts' | 'costVariance' | 'revenueVariance' | 'hoursVariance' | 'costPercent' | 'revenuePercent' | 'hoursPercent' | 'remainingCosts' | 'remainingRevenue' | 'remainingHours' | 'forecastCosts' | 'forecastRevenue';
  label: string;
  visible: boolean;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
  category: 'identification' | 'financial' | 'time' | 'calculated' | 'metadata';
}

const DEFAULT_COLUMNS: ColumnDefinition[] = [
  // Identification
  { key: 'code', label: 'Code', visible: true, sortable: true, align: 'left', category: 'identification' },
  { key: 'name', label: 'Name', visible: true, sortable: true, align: 'left', category: 'identification' },
  { key: 'type', label: 'Type', visible: true, sortable: true, align: 'left', category: 'identification' },
  { key: 'status', label: 'Status', visible: true, sortable: true, align: 'left', category: 'identification' },
  { key: 'billable', label: 'Billable', visible: true, sortable: true, align: 'center', category: 'identification' },
  
  // Financial - Budget & Actual
  { key: 'costsBudget', label: 'Costs Budget', visible: true, sortable: true, align: 'right', category: 'financial' },
  { key: 'actualCosts', label: 'Actual Costs', visible: true, sortable: true, align: 'right', category: 'financial' },
  { key: 'committedCosts', label: 'Committed Costs', visible: true, sortable: true, align: 'right', category: 'financial' },
  { key: 'totalCosts', label: 'Total Costs', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'costVariance', label: 'Cost Variance', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'costPercent', label: 'Cost %', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'remainingCosts', label: 'Remaining Costs', visible: false, sortable: true, align: 'right', category: 'calculated' },
  { key: 'forecastCosts', label: 'Forecast Costs', visible: false, sortable: true, align: 'right', category: 'calculated' },
  
  // Financial - Revenue
  { key: 'revenueBudget', label: 'Revenue Budget', visible: true, sortable: true, align: 'right', category: 'financial' },
  { key: 'actualRevenue', label: 'Actual Revenue', visible: true, sortable: true, align: 'right', category: 'financial' },
  { key: 'revenueVariance', label: 'Revenue Variance', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'revenuePercent', label: 'Revenue %', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'remainingRevenue', label: 'Remaining Revenue', visible: false, sortable: true, align: 'right', category: 'calculated' },
  { key: 'forecastRevenue', label: 'Forecast Revenue', visible: false, sortable: true, align: 'right', category: 'calculated' },
  
  // Time Metrics
  { key: 'hoursBudget', label: 'Hours Budget', visible: true, sortable: true, align: 'right', category: 'time' },
  { key: 'actualHours', label: 'Actual Hours', visible: true, sortable: true, align: 'right', category: 'time' },
  { key: 'hoursVariance', label: 'Hours Variance', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'hoursPercent', label: 'Hours %', visible: true, sortable: true, align: 'right', category: 'calculated' },
  { key: 'remainingHours', label: 'Remaining Hours', visible: false, sortable: true, align: 'right', category: 'calculated' },
  
  // Metadata
  { key: 'owner', label: 'Owner', visible: false, sortable: true, align: 'left', category: 'metadata' },
  { key: 'lastUpdated', label: 'Last Updated', visible: false, sortable: true, align: 'left', category: 'metadata' },
];

interface CostCentresTableProps {
  data: CostCentre[];
  columns?: ColumnDefinition[];
  onColumnConfigChange?: (columns: ColumnDefinition[]) => void;
  onRowClick?: (costCentre: CostCentre) => void;
  loading?: boolean;
  useCardWrapper?: boolean; // Feature flag to enable/disable card wrapper
}

type SortDirection = 'asc' | 'desc' | null;

export function CostCentresTable({ 
  data,
  columns = DEFAULT_COLUMNS,
  onRowClick,
  loading = false,
  useCardWrapper = true, // Default to true for new behaviour
}: CostCentresTableProps) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Dynamic height calculation
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const dynamicHeight = useDynamicTableHeight(tableContainerRef, {
    minHeight: 400,
    bottomPadding: 24,
  });
  
  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  // Calculate derived values
  const calculateTotalCosts = (row: CostCentre) => row.actualCosts + row.committedCosts;
  const calculateCostVariance = (row: CostCentre) => row.costsBudget - row.actualCosts;
  const calculateRevenueVariance = (row: CostCentre) => row.revenueBudget - row.actualRevenue;
  const calculateHoursVariance = (row: CostCentre) => row.hoursBudget - row.actualHours;
  const calculateCostPercent = (row: CostCentre) => row.costsBudget > 0 ? (row.actualCosts / row.costsBudget) * 100 : 0;
  const calculateRevenuePercent = (row: CostCentre) => row.revenueBudget > 0 ? (row.actualRevenue / row.revenueBudget) * 100 : 0;
  const calculateHoursPercent = (row: CostCentre) => row.hoursBudget > 0 ? (row.actualHours / row.hoursBudget) * 100 : 0;
  const calculateRemainingCosts = (row: CostCentre) => row.costsBudget - row.actualCosts;
  const calculateRemainingRevenue = (row: CostCentre) => row.revenueBudget - row.actualRevenue;
  const calculateRemainingHours = (row: CostCentre) => row.hoursBudget - row.actualHours;
  const calculateForecastCosts = (row: CostCentre) => {
    const percent = calculateCostPercent(row);
    return percent > 0 ? (row.actualCosts / percent) * 100 : row.actualCosts;
  };
  const calculateForecastRevenue = (row: CostCentre) => {
    const percent = calculateRevenuePercent(row);
    return percent > 0 ? (row.actualRevenue / percent) * 100 : row.actualRevenue;
  };

  // Get value for sorting and display
  const getCellValue = (row: CostCentre, key: string): number | string | boolean => {
    switch (key) {
      case 'totalCosts': return calculateTotalCosts(row);
      case 'costVariance': return calculateCostVariance(row);
      case 'revenueVariance': return calculateRevenueVariance(row);
      case 'hoursVariance': return calculateHoursVariance(row);
      case 'costPercent': return calculateCostPercent(row);
      case 'revenuePercent': return calculateRevenuePercent(row);
      case 'hoursPercent': return calculateHoursPercent(row);
      case 'remainingCosts': return calculateRemainingCosts(row);
      case 'remainingRevenue': return calculateRemainingRevenue(row);
      case 'remainingHours': return calculateRemainingHours(row);
      case 'forecastCosts': return calculateForecastCosts(row);
      case 'forecastRevenue': return calculateForecastRevenue(row);
      default: return row[key as keyof CostCentre] as number | string | boolean;
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) return data;

    return [...data].sort((a, b) => {
      const aValue = getCellValue(a, sortColumn);
      const bValue = getCellValue(b, sortColumn);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1)
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortDirection === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [data, sortColumn, sortDirection]);

  // Pagination calculations
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  // Calculate totals
  const totals = useMemo(() => {
    return data.reduce((acc, row) => ({
      costsBudget: acc.costsBudget + row.costsBudget,
      actualCosts: acc.actualCosts + row.actualCosts,
      committedCosts: acc.committedCosts + row.committedCosts,
      totalCosts: acc.totalCosts + calculateTotalCosts(row),
      revenueBudget: acc.revenueBudget + row.revenueBudget,
      actualRevenue: acc.actualRevenue + row.actualRevenue,
      hoursBudget: acc.hoursBudget + row.hoursBudget,
      actualHours: acc.actualHours + row.actualHours,
    }), {
      costsBudget: 0,
      actualCosts: 0,
      committedCosts: 0,
      totalCosts: 0,
      revenueBudget: 0,
      actualRevenue: 0,
      hoursBudget: 0,
      actualHours: 0,
    });
  }, [data]);

  const totalCostVariance = totals.costsBudget - totals.actualCosts;
  const totalRevenueVariance = totals.revenueBudget - totals.actualRevenue;
  const totalHoursVariance = totals.hoursBudget - totals.actualHours;
  const totalCostPercent = totals.costsBudget > 0 ? (totals.actualCosts / totals.costsBudget) * 100 : 0;
  const totalRevenuePercent = totals.revenueBudget > 0 ? (totals.actualRevenue / totals.revenueBudget) * 100 : 0;
  const totalHoursPercent = totals.hoursBudget > 0 ? (totals.actualHours / totals.hoursBudget) * 100 : 0;

  // Handle sort
  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Render cell content
  const renderCell = (row: CostCentre, column: ColumnDefinition) => {
    const value = getCellValue(row, column.key);

    switch (column.key) {
      case 'status':
        const statusValue = value as string;
        return (
          <Badge className={getStatusColor(statusValue)}>
            {statusValue || '-'}
          </Badge>
        );
      
      case 'billable':
        return (
          <div className="flex justify-center">
            <Badge variant={value ? 'default' : 'secondary'}>
              {value ? 'Yes' : 'No'}
            </Badge>
          </div>
        );
      
      case 'costsBudget':
      case 'actualCosts':
      case 'committedCosts':
      case 'totalCosts':
      case 'revenueBudget':
      case 'actualRevenue':
      case 'remainingCosts':
      case 'remainingRevenue':
      case 'forecastCosts':
      case 'forecastRevenue':
        return formatCurrency(value as number);
      
      case 'costVariance':
      case 'revenueVariance':
        return (
          <span className={getVarianceColor(value as number)}>
            {value as number > 0 ? '+' : ''}{formatCurrency(value as number)}
          </span>
        );
      
      case 'hoursVariance':
        return (
          <span className={getVarianceColor(value as number)}>
            {value as number > 0 ? '+' : ''}{formatHours(value as number)}
          </span>
        );
      
      case 'costPercent':
      case 'revenuePercent':
      case 'hoursPercent':
        const percent = value as number;
        const isOverBudget = percent > 100;
        return (
          <div className="flex items-center gap-2">
            <span className={cn(
              isOverBudget && 'text-error-600 dark:text-error-500 font-medium'
            )}>
              {formatPercent(percent)}
            </span>
            {isOverBudget && (
              <Badge variant="destructive" className="text-xs">
                Over
              </Badge>
            )}
          </div>
        );
      
      case 'hoursBudget':
      case 'actualHours':
      case 'remainingHours':
        return formatHours(value as number);
      
      case 'lastUpdated':
        return value ? new Date(value as string | Date).toLocaleDateString('en-GB') : '-';
      
      default:
        return value?.toString() || '-';
    }
  };

  // Render total cell
  const renderTotalCell = (column: ColumnDefinition) => {
    switch (column.key) {
      case 'code':
        return <span className="font-bold">Total</span>;
      
      case 'name':
      case 'type':
      case 'status':
      case 'billable':
      case 'owner':
      case 'lastUpdated':
        return '-';
      
      case 'costsBudget':
        return formatCurrency(totals.costsBudget);
      
      case 'actualCosts':
        return formatCurrency(totals.actualCosts);
      
      case 'committedCosts':
        return formatCurrency(totals.committedCosts);
      
      case 'totalCosts':
        return formatCurrency(totals.totalCosts);
      
      case 'costVariance':
        return (
          <span className={getVarianceColor(totalCostVariance)}>
            {totalCostVariance > 0 ? '+' : ''}{formatCurrency(totalCostVariance)}
          </span>
        );
      
      case 'costPercent':
        return (
          <span className={cn(
            totalCostPercent > 100 && 'text-error-600 dark:text-error-500 font-medium'
          )}>
            {formatPercent(totalCostPercent)}
          </span>
        );
      
      case 'revenueBudget':
        return formatCurrency(totals.revenueBudget);
      
      case 'actualRevenue':
        return formatCurrency(totals.actualRevenue);
      
      case 'revenueVariance':
        return (
          <span className={getVarianceColor(totalRevenueVariance)}>
            {totalRevenueVariance > 0 ? '+' : ''}{formatCurrency(totalRevenueVariance)}
          </span>
        );
      
      case 'revenuePercent':
        return (
          <span className={cn(
            totalRevenuePercent > 100 && 'text-error-600 dark:text-error-500 font-medium'
          )}>
            {formatPercent(totalRevenuePercent)}
          </span>
        );
      
      case 'hoursBudget':
        return formatHours(totals.hoursBudget);
      
      case 'actualHours':
        return formatHours(totals.actualHours);
      
      case 'hoursVariance':
        return (
          <span className={getVarianceColor(totalHoursVariance)}>
            {totalHoursVariance > 0 ? '+' : ''}{formatHours(totalHoursVariance)}
          </span>
        );
      
      case 'hoursPercent':
        return (
          <span className={cn(
            totalHoursPercent > 100 && 'text-error-600 dark:text-error-500 font-medium'
          )}>
            {formatPercent(totalHoursPercent)}
          </span>
        );
      
      case 'remainingCosts':
        return formatCurrency(totals.costsBudget - totals.actualCosts);
      
      case 'remainingRevenue':
        return formatCurrency(totals.revenueBudget - totals.actualRevenue);
      
      case 'remainingHours':
        return formatHours(totals.hoursBudget - totals.actualHours);
      
      case 'forecastCosts':
      case 'forecastRevenue':
        return '-';
      
      default:
        return '-';
    }
  };

  const visibleColumns = columns.filter(col => col.visible);

  // Define pinned columns (Code, Name, Type, Status)
  const pinnedColumnKeys = ['code', 'name', 'type', 'status'];
  
  // Helper to check if column is pinned
  const isPinnedColumn = (columnKey: string) => pinnedColumnKeys.includes(columnKey);
  
  // Get sticky column class based on column key
  const getStickyColumnClass = (columnKey: string) => {
    switch (columnKey) {
      case 'code':
        return 'sticky left-0 z-10 bg-background';
      case 'name':
        return 'sticky left-[120px] z-10 bg-background';
      case 'type':
        return 'sticky left-[320px] z-10 bg-background';
      case 'status':
        return 'sticky left-[440px] z-10 bg-background shadow-[2px_0_8px_-2px_rgba(0,0,0,0.1)]';
      default:
        return '';
    }
  };
  
  // Get column width class
  const getColumnWidthClass = (columnKey: string) => {
    switch (columnKey) {
      case 'code':
        return 'w-[120px] min-w-[120px]';
      case 'name':
        return 'w-[200px] min-w-[200px]';
      case 'type':
        return 'w-[120px] min-w-[120px]';
      case 'status':
        return 'w-[150px] min-w-[150px]';
      default:
        return '';
    }
  };

  // Table content component
  const tableContent = (
    <div 
      ref={tableContainerRef}
      className="rounded-md border overflow-auto"
    >
      <Table className="w-max min-w-full">
        <TableHeader className="sticky top-0 z-30 bg-background">
          <TableRow>
            {visibleColumns.map((column) => (
              <TableHead
                key={column.key}
                className={cn(
                  'py-3 bg-background',
                  column.align === 'right' && 'text-right',
                  column.align === 'center' && 'text-center',
                  column.sortable && 'cursor-pointer select-none hover:bg-muted/50',
                  getStickyColumnClass(column.key),
                  getColumnWidthClass(column.key),
                  isPinnedColumn(column.key) && 'z-40'
                )}
                onClick={() => column.sortable && handleSort(column.key)}
              >
                <div className={cn(
                  'flex items-center gap-2',
                  column.align === 'right' && 'justify-end',
                  column.align === 'center' && 'justify-center'
                )}>
                  <span>{column.label}</span>
                  {column.sortable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0"
                    >
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-50" />
                      )}
                    </Button>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            // Loading skeleton
            Array.from({ length: pageSize }).map((_, i) => (
              <TableRow key={i}>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'py-3',
                      getStickyColumnClass(column.key),
                      getColumnWidthClass(column.key)
                    )}
                  >
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : totalItems === 0 ? (
            // Empty state
            <TableRow>
              <TableCell 
                colSpan={visibleColumns.length} 
                className="text-center text-muted-foreground py-12"
              >
                <div className="flex flex-col items-center gap-2">
                  <p className="text-lg font-medium">No cost centres found</p>
                  <p className="text-sm">Get started by creating your first cost centre</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            <>
              {paginatedData.map((row) => (
                <TableRow 
                  key={row.id}
                  className={cn(onRowClick && "cursor-pointer hover:bg-muted/50")}
                  onClick={() => onRowClick?.(row)}
                >
                  {visibleColumns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        'py-3',
                        column.align === 'right' && 'text-right',
                        column.align === 'center' && 'text-center',
                        getStickyColumnClass(column.key),
                        getColumnWidthClass(column.key)
                      )}
                    >
                      {renderCell(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {/* Totals Row */}
              <TableRow className="font-bold bg-muted/50 hover:bg-muted/50 sticky bottom-0 z-20">
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'py-3 bg-muted/50',
                      column.align === 'right' && 'text-right',
                      column.align === 'center' && 'text-center',
                      getStickyColumnClass(column.key),
                      getColumnWidthClass(column.key),
                      isPinnedColumn(column.key) && 'bg-muted'
                    )}
                  >
                    {renderTotalCell(column)}
                  </TableCell>
                ))}
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );

  // Pagination controls
  const paginationControls = totalItems > 0 && (
    <div className="flex items-center justify-between gap-4 px-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} cost centres
        </span>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Return with or without Card wrapper based on feature flag
  if (useCardWrapper) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {totalItems} Cost Centre{totalItems !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {tableContent}
          {paginationControls}
        </CardContent>
      </Card>
    );
  }

  // Legacy layout without Card wrapper
  return (
    <div className="space-y-4">
      {tableContent}
      {paginationControls}
    </div>
  );
}

export { DEFAULT_COLUMNS as DEFAULT_COST_CENTRE_COLUMNS };
export type { ColumnDefinition as CostCentreColumnDefinition };
